/* ==========================================================================
   SOFLICARE — theme.js
   No frameworks. Small, legible modules; every behavior degrades gracefully.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.remove('no-js');
  if (!('IntersectionObserver' in window)) root.classList.add('no-io');

  var routes = window.SOFLI && window.SOFLI.routes ? window.SOFLI.routes : {
    cart_add: '/cart/add.js',
    cart_change: '/cart/change.js',
    cart: '/cart.js',
    root: '/'
  };
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Perceived performance: mark images loaded (capture phase catches dynamic ones too) */
  function markLoaded(e) {
    if (e.target && e.target.tagName === 'IMG') e.target.classList.add('is-loaded');
  }
  doc.addEventListener('load', markLoaded, true);
  doc.addEventListener('error', markLoaded, true);
  Array.prototype.forEach.call(doc.images, function (img) {
    if (img.complete) img.classList.add('is-loaded');
  });

  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { el && el.addEventListener(ev, fn, opts || false); }

  /* ------------------------------------------------------------------
     Sticky header — shadow on scroll, and on phones, yields screen
     space by hiding on scroll-down and returning on scroll-up.
  ------------------------------------------------------------------ */
  (function stickyHeader() {
    var hdr = qs('[data-header]');
    if (!hdr) return;
    var lastY = window.scrollY;
    var ticking = false;
    var isMobile = function () { return window.matchMedia('(max-width: 989px)').matches; };

    function update() {
      var y = window.scrollY;
      hdr.classList.toggle('is-stuck', y > 6);

      if (isMobile() && !root.classList.contains('lock')) {
        var goingDown = y > lastY;
        if (goingDown && y > 120) hdr.classList.add('is-hidden');
        else if (!goingDown) hdr.classList.remove('is-hidden');
      } else {
        hdr.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    on(window, 'scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  })();

  /* ------------------------------------------------------------------
     Drawers & modals
     Openers:  [data-drawer-open="id"]
     Panels:   [data-drawer="id"]  (with .drw markup)
  ------------------------------------------------------------------ */
  var Drawers = (function () {
    var active = null;
    var lastFocus = null;
    var savedY = 0;

    /* iOS Safari ignores `overflow: hidden` for scroll locking, so the page
       keeps moving behind an open sheet. Pinning the body and restoring the
       exact offset on close is the only approach that holds on iPhone. */
    function lockScroll() {
      if (root.classList.contains('lock')) return;
      savedY = window.scrollY;
      root.classList.add('lock');
      doc.body.style.position = 'fixed';
      doc.body.style.top = (-savedY) + 'px';
      doc.body.style.left = '0';
      doc.body.style.right = '0';
      doc.body.style.width = '100%';
    }

    function unlockScroll() {
      if (!root.classList.contains('lock')) return;
      root.classList.remove('lock');
      doc.body.style.position = '';
      doc.body.style.top = '';
      doc.body.style.left = '';
      doc.body.style.right = '';
      doc.body.style.width = '';
      /* `scroll-behavior: smooth` would otherwise animate the restore and the
         page would visibly slide. Force it instant, then hand styling back. */
      var prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, savedY);
      root.style.scrollBehavior = prevBehavior;
    }
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function open(id, opener) {
      var drw = qs('[data-drawer="' + id + '"]');
      if (!drw || drw === active) return;
      if (active) close(true);
      lastFocus = opener || doc.activeElement;
      active = drw;
      drw.classList.add('is-open');
      drw.removeAttribute('aria-hidden');
      lockScroll();
      var target = qs('[autofocus]', drw) || qs(FOCUSABLE, drw) || drw;
      window.setTimeout(function () { target.focus({ preventScroll: true }); }, reducedMotion ? 0 : 120);
      on(doc, 'keydown', onKey);
    }

    function close(keepLock) {
      if (!active) return;
      active.classList.remove('is-open');
      active.setAttribute('aria-hidden', 'true');
      active = null;
      if (!keepLock) unlockScroll();
      doc.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      lastFocus = null;
    }

    function onKey(e) {
      if (!active) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = qsa(FOCUSABLE, active).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && doc.activeElement === last) { first.focus(); e.preventDefault(); }
    }

    on(doc, 'click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        open(opener.getAttribute('data-drawer-open'), opener);
        return;
      }
      if (e.target.closest('[data-drawer-close]') || e.target.matches('.drw__overlay')) {
        e.preventDefault();
        close();
      }
    });

    qsa('[data-drawer]').forEach(function (d) { d.setAttribute('aria-hidden', 'true'); });

    return { open: open, close: close };
  })();

  /* ------------------------------------------------------------------
     Scroll reveal
  ------------------------------------------------------------------ */
  (function reveal() {
    var items = qsa('[data-reveal]');
    if (!items.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  })();

  /* ------------------------------------------------------------------
     Hero review chip — the quote is clamped to a compact height by
     default; reveal a "Read more" toggle only when that clamp actually
     truncates the text, so short quotes show in full with no button and
     longer ones get a genuine way to read the rest (never a silent
     mid-sentence cut-off).
  ------------------------------------------------------------------ */
  (function heroChipReveal() {
    var chip = qs('.hero__chip');
    if (!chip) return;
    var quote = qs('blockquote', chip);
    if (!quote) return;
    var btn = null;

    function sync() {
      if (chip.classList.contains('is-open')) return;
      var overflowing = quote.scrollHeight > quote.clientHeight + 1;
      if (overflowing && !btn) {
        btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'hero__chip-more';
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = 'Read more';
        on(btn, 'click', function () {
          var open = chip.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          btn.textContent = open ? 'Show less' : 'Read more';
        });
        chip.appendChild(btn);
      } else if (!overflowing && btn) {
        btn.remove();
        btn = null;
      }
    }

    sync();
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(sync);

    var resizeTimer;
    on(window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 150);
    });
  })();

  /* ------------------------------------------------------------------
     Sliders — scroll-snap tracks with arrows, dots, active state.
     [data-slider] wraps [data-slider-track] > slides
  ------------------------------------------------------------------ */
  qsa('[data-slider]').forEach(function (slider) {
    var track = qs('[data-slider-track]', slider);
    if (!track) return;
    var slides = qsa(':scope > *', track);
    if (slides.length < 2) {
      qsa('[data-slider-prev], [data-slider-next], [data-slider-dots]', slider).forEach(function (el) { el.hidden = true; });
      return;
    }
    var prev = qs('[data-slider-prev]', slider);
    var next = qs('[data-slider-next]', slider);
    var dotsWrap = qs('[data-slider-dots]', slider);
    var dots = [];
    var current = 0;

    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var b = doc.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        on(b, 'click', function () { goTo(i); });
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }

    function goTo(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    function setCurrent(i) {
      current = i;
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
      slider.dispatchEvent(new CustomEvent('slider:change', { detail: { index: i } }));
    }
    on(prev, 'click', function () { goTo(current - 1); });
    on(next, 'click', function () { goTo(current + 1); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) setCurrent(slides.indexOf(entry.target));
      });
    }, { root: track, threshold: 0.55 });
    slides.forEach(function (s) { io.observe(s); });
    setCurrent(0);

    slider.goTo = goTo;
  });

  /* ------------------------------------------------------------------
     Product gallery thumbnails
  ------------------------------------------------------------------ */
  qsa('[data-gallery]').forEach(function (gallery) {
    var thumbs = qsa('[data-gallery-thumb]', gallery);
    if (!thumbs.length) return;
    thumbs.forEach(function (t, i) {
      on(t, 'click', function () { if (gallery.goTo) gallery.goTo(i); });
    });
    on(gallery, 'slider:change', function (e) {
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === e.detail.index);
        t.setAttribute('aria-current', i === e.detail.index ? 'true' : 'false');
      });
    });
  });

  /* ------------------------------------------------------------------
     Accordions — animated height on .accord blocks
  ------------------------------------------------------------------ */
  /* Height animation is pure CSS (grid-template-rows 0fr -> 1fr), so this
     handler only flips state — no scrollHeight reads, no forced reflow. */
  on(doc, 'click', function (e) {
    var summary = e.target.closest('[data-accord-toggle]');
    if (!summary) return;
    e.preventDefault();
    var accord = summary.closest('.accord');
    var isOpen = accord.classList.contains('is-open');
    accord.classList.toggle('is-open', !isOpen);
    summary.setAttribute('aria-expanded', String(!isOpen));
  });

  /* ------------------------------------------------------------------
     Quantity steppers (event delegation — works inside re-rendered cart)
  ------------------------------------------------------------------ */
  on(doc, 'click', function (e) {
    var btn = e.target.closest('[data-qty-plus], [data-qty-minus]');
    if (!btn) return;
    var input = qs('input', btn.closest('.qty'));
    if (!input) return;
    var val = parseInt(input.value, 10) || 1;
    var min = parseInt(input.min, 10) || 1;
    input.value = btn.hasAttribute('data-qty-plus') ? val + 1 : Math.max(min, val - 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     Cart — section re-render pipeline
  ------------------------------------------------------------------ */
  var Cart = (function () {
    function swapDrawer(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = qs('[data-cart-drawer-content]', parsed);
      var target = qs('[data-cart-drawer-content]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      var countSrc = qs('[data-cart-count-source]', parsed);
      if (countSrc) setCount(countSrc.textContent.trim());
    }
    function setCount(n) {
      qsa('[data-cart-count]').forEach(function (el) {
        el.textContent = n;
        el.setAttribute('data-count', n);
      });
      /* Keep the spoken label grammatical: "1 item", not "1 items". */
      qsa('[data-cart-count-text]').forEach(function (el) {
        el.textContent = n + (String(n) === '1' ? ' item' : ' items');
      });
    }

    function announce(message) {
      var status = qs('[data-cart-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(function () { status.textContent = message; }, 60);
    }
    function refresh() {
      return fetch(routes.root + '?sections=cart-drawer')
        .then(function (r) { return r.json(); })
        .then(function (data) { if (data['cart-drawer']) swapDrawer(data['cart-drawer']); })
        .catch(function () { /* silent — cart page remains the fallback */ });
    }
    function add(formData) {
      return fetch(routes.cart_add, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return refresh();
        });
      });
    }
    function change(line, quantity) {
      return fetch(routes.cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function () { return refresh(); });
    }
    return { add: add, change: change, refresh: refresh, announce: announce };
  })();

  /* Drawer line controls (delegated — survives re-render) */
  on(doc, 'click', function (e) {
    var btn = e.target.closest('[data-cd-change]');
    if (!btn) return;
    e.preventDefault();

    var line = parseInt(btn.getAttribute('data-line'), 10);
    var action = btn.getAttribute('data-cd-action');
    var title = btn.getAttribute('data-title') || '';
    var drawerBody = btn.closest('[data-cart-drawer-content]');
    if (drawerBody) drawerBody.setAttribute('aria-busy', 'true');

    Cart.change(line, parseInt(btn.getAttribute('data-quantity'), 10))
      .then(function () {
        var fresh = qs('[data-cart-drawer-content]');
        if (fresh) fresh.removeAttribute('aria-busy');

        /* The controls were destroyed by the re-render. Without this, focus
           falls to <body> and a keyboard or screen-reader user loses their
           place entirely. Return them to the equivalent control. */
        var target = qs('[data-cd-change][data-line="' + line + '"][data-cd-action="' + action + '"]');
        if (!target) {
          var remaining = qsa('[data-cd-change][data-cd-action="remove"]');
          target = remaining.length ? remaining[remaining.length - 1] : qs('[data-drawer="cart"] [data-drawer-close]');
        }
        if (target && target.focus) target.focus({ preventScroll: true });

        Cart.announce(action === 'remove'
          ? (title ? title + ' removed from your cart.' : 'Item removed from your cart.')
          : 'Cart updated.');
      })
      .catch(function () {
        if (drawerBody) drawerBody.removeAttribute('aria-busy');
        Cart.announce('Sorry, we could not update your cart.');
      });
  });

  /* ------------------------------------------------------------------
     Product form — variant state + AJAX add to cart
  ------------------------------------------------------------------ */
  qsa('[data-product]').forEach(function (scope) {
    var dataEl = qs('[data-variants-json]', scope);
    var form = qs('[data-product-form]', scope);
    if (!form) return;
    var variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    var idInput = qs('input[name="id"]', form);
    var priceEls = qsa('[data-price]', scope);
    var compareEls = qsa('[data-compare]', scope);
    var stockEl = qs('[data-stock]', scope);
    var btn = qs('[data-atc]', scope);
    var btnLabel = btn ? qs('[data-atc-label]', btn) : null;
    var errEl = qs('[data-form-error]', scope);
    var stickyBtn = qs('[data-sticky-submit]', scope);

    function selectedOptions() {
      var opts = [];
      qsa('[data-option-index]', scope).forEach(function (group) {
        var checked = qs('input:checked', group);
        if (checked) opts[parseInt(group.getAttribute('data-option-index'), 10)] = checked.value;
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    function render(variant) {
      var available = variant && variant.available;
      if (variant && idInput) idInput.value = variant.id;
      priceEls.forEach(function (el) { el.textContent = variant ? variant.price : ''; });
      compareEls.forEach(function (el) {
        el.hidden = !(variant && variant.compare_at_price);
        if (variant && variant.compare_at_price) el.textContent = variant.compare_at_price;
      });
      if (stockEl) {
        var threshold = parseInt(stockEl.getAttribute('data-threshold'), 10) || 0;
        var isLow = available && threshold > 0 && variant && variant.managed && variant.qty > 0 && variant.qty <= threshold;
        stockEl.classList.toggle('is-out', !available);
        stockEl.classList.toggle('is-low', isLow);
        if (!available) {
          stockEl.textContent = stockEl.getAttribute('data-out-text');
        } else if (isLow) {
          stockEl.textContent = stockEl.getAttribute('data-low-text').replace('[n]', variant.qty);
        } else {
          stockEl.textContent = stockEl.getAttribute('data-in-text');
        }
      }
      [btn, stickyBtn].forEach(function (b) {
        if (!b) return;
        b.toggleAttribute('disabled', !available);
      });
      if (btnLabel) btnLabel.textContent = available ? btnLabel.getAttribute('data-add-text') : btnLabel.getAttribute('data-out-text');
      if (variant && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    on(scope, 'change', function (e) {
      if (!e.target.closest('[data-option-index]')) return;
      render(findVariant(selectedOptions()));
    });

    on(form, 'submit', function (e) {
      e.preventDefault();
      if (btn) btn.classList.add('is-busy');
      if (errEl) { errEl.classList.remove('is-visible'); errEl.textContent = ''; }
      Cart.add(new FormData(form))
        .then(function () {
          if (window.SOFLI && window.SOFLI.cartDrawer === false) {
            window.location.href = routes.cart_url || '/cart';
            return;
          }
          Drawers.open('cart');
        })
        .catch(function (err) {
          if (errEl) {
            errEl.textContent = (err && err.description) ||
              (window.SOFLI.strings && window.SOFLI.strings.genericError) ||
              'Something went wrong — please try again.';
            errEl.classList.add('is-visible');
          }
        })
        .then(function () { if (btn) btn.classList.remove('is-busy'); });
    });

    /* Sticky bar submits the main form */
    if (stickyBtn) {
      on(stickyBtn, 'click', function () {
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    /* Sticky bar visibility */
    var anchor = qs('[data-atc-anchor]', scope);
    var bar = qs('[data-sticky-atc]', scope);
    if (anchor && bar && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var below = entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', !entry.isIntersecting && below);
      }, { threshold: 0 });
      io.observe(anchor);
    }
  });

  /* ------------------------------------------------------------------
     Product recommendations (Shopify recommendations + section render)
  ------------------------------------------------------------------ */
  qsa('[data-recommendations]').forEach(function (el) {
    var url = el.getAttribute('data-url');
    if (!url) return;

    /* Deferred until the block nears the viewport: cross-sells are never the
       LCP element, so this frees bandwidth for the hero during initial load. */
    if ('IntersectionObserver' in window) {
      var recIo = new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        loadRecs();
      }, { rootMargin: '600px 0px' });
      recIo.observe(el);
    } else {
      loadRecs();
    }

    function loadRecs() {
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var parsed = new DOMParser().parseFromString(text, 'text/html');
        var inner = qs('[data-recommendations-inner]', parsed);
        if (inner && inner.children.length) {
          el.innerHTML = inner.outerHTML;
        } else {
          var section = el.closest('.shopify-section');
          (section || el).style.display = 'none';
        }
      })
      .catch(function () {});
    }
  });

  /* ------------------------------------------------------------------
     Bottom sheets (phones): drag the handle down to dismiss
  ------------------------------------------------------------------ */
  if (window.matchMedia('(max-width: 749px)').matches) {
    qsa('[data-drawer]').forEach(function (drw) {
      var panel = qs('.drw__panel', drw);
      var head = qs('.drw__head', drw);
      if (!panel || !head) return;
      var startY = 0, dy = 0, dragging = false;
      head.addEventListener('touchstart', function (e) {
        dragging = true; dy = 0;
        startY = e.touches[0].clientY;
        panel.style.transition = 'none';
      }, { passive: true });
      head.addEventListener('touchmove', function (e) {
        if (!dragging) return;
        dy = Math.max(0, e.touches[0].clientY - startY);
        panel.style.transform = 'translateY(' + dy + 'px)';
      }, { passive: true });
      head.addEventListener('touchend', function () {
        if (!dragging) return;
        dragging = false;
        panel.style.transition = '';
        panel.style.transform = '';
        if (dy > 80) Drawers.close();
        dy = 0;
      });
    });
  }

})();
